import { initContinuationDots } from "./home/continuation-dots.js";
import { attachBlobInset } from "./home/blob-inset.js";
import { initWhereStatus } from "./where-widget.js";
import { initFabric } from "./home/fabric.js?v=tailored-material-1";
import { revealMasks } from "./home/reveal-mask-urls.js";

let finishHero;
const heroReady = new Promise((resolve) => { finishHero = resolve; });
let heroSettled = false;
function settleHero() {
  if (heroSettled) return;
  heroSettled = true;
  performance.mark("hero-ready");
  finishHero();
  window.dispatchEvent(new Event("heroready"));
}
const afterPaint = () => new Promise((resolve) =>
  requestAnimationFrame(() => requestAnimationFrame(resolve)));

heroReady.then(() => {
  const status = document.querySelector("[data-where-status]");
  if (!status) return;
  const observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    initWhereStatus();
  }, { rootMargin: "240px" });
  observer.observe(status);
});

const horizonRevealLoopUrl = new URL("./assets/horizon-worlds/reveal-loop.mp4", import.meta.url).href;
const horizonRevealLoopMobileUrl = new URL("./assets/horizon-worlds/reveal-loop-mobile.mp4", import.meta.url).href;
const horizonRevealPosterUrl = new URL("./assets/horizon-worlds/reveal-poster.webp", import.meta.url).href;
const definitelyGamesTeaserUrl = new URL("./assets/definitely-games-teaser.webp", import.meta.url).href;
const reflektorPreviewUrl = new URL("./assets/reflektor-preview.webp", import.meta.url).href;
const reflektorDetailPosterUrl = new URL("./assets/reflektor/washington-square-full-poster.webp", import.meta.url).href;
const bondiRevealLoopUrl = new URL("./assets/bondi/paint-submenus.mp4", import.meta.url).href;
const bondiRevealLoopMobileUrl = new URL("./assets/bondi/paint-submenus-mobile.mp4", import.meta.url).href;
const bondiRevealPosterUrl = new URL("./assets/bondi/paint-submenus-poster.webp", import.meta.url).href;
const vrbFotoLoopUrl = new URL("./assets/vrb-foto-ui-loop-clean-19.mp4", import.meta.url).href;
const vrbFotoLoopMobileUrl = new URL("./assets/vrb-foto-ui-loop-clean-19-mobile.mp4", import.meta.url).href;
const vrbFotoPosterUrl = new URL("./assets/vrb-foto-ui-poster.webp", import.meta.url).href;

async function startHero() {
  // Paint the blue page before starting the existing off-thread renderer.
  await afterPaint();
  performance.mark("hero-surface-ready");
  if (matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData) {
    document.body.classList.add("is-hero-static");
    document.documentElement.classList.remove("is-project-anchor-load");
    performance.mark("hero-static");
    settleHero();
    return;
  }
  initFabric({
    canvas: document.getElementById("fabric-canvas"),
    hero: document.getElementById("top"),
    async onReady() {
      if (document.body.classList.contains("no-webgl")) return;
      // This first live frame is exactly the page blue. Present it before the
      // worker starts raising the lettering, so none of the entrance is hidden.
      document.documentElement.classList.add("is-hero-ready");
      document.documentElement.classList.remove("is-project-anchor-load");
      await afterPaint();
      performance.mark("hero-interactive");
    },
    onRevealed() {
      document.documentElement.classList.add("is-hero-revealed");
      performance.mark("hero-emerged");
      settleHero();
    },
    onUnavailable() {
      document.body.classList.add("no-webgl");
      document.documentElement.classList.remove("is-hero-ready", "is-hero-revealed", "is-project-anchor-load");
      performance.mark("hero-static");
      settleHero();
    },
  });
}
startHero();

heroReady.then(initContinuationDots);

(() => {
  const projects = [
    {
      id: "horizon-worlds",
      number: "01",
      title: "Horizon Worlds",
      company: "Meta",
      date: "2019–2022",
      artWord: "HORIZON",
      artClass: "horizon",
      previewVideo: horizonRevealLoopUrl,
      previewVideoMobile: horizonRevealLoopMobileUrl,
      previewPoster: horizonRevealPosterUrl,
      previewWidth: 1280,
      previewHeight: 720,
      detailPoster: horizonRevealPosterUrl,
      href: "./projects/horizon-worlds/",
      summary:
        "Joining at prototype stage, building the design organization, and leading the product through shipment.",
      role: "Head of Design, Horizon Worlds / Design Manager (M2)",
      context: "A new category, a changing product surface, and a large cross-functional team",
      outcome: "A shipped product, a design organization, and a more coherent experience system",
      challenge:
        "The hard part is usually not components. It is getting teams to see the same product from the same altitude.",
      moves: [
        "Mapped the messy surface area into a few memorable principles.",
        "Built working examples so the system felt usable before it felt official.",
        "Created critique rituals and decision rules that reduced reinvention.",
      ],
      artifacts: [
        "Principles deck",
        "Component/pattern map",
        "Prototype gallery",
        "Adoption notes",
      ],
    },
    {
      id: "reflektor",
      number: "02",
      title: "Reflektor",
      company: "Living Cities",
      date: "2022–2024",
      artWord: "REFLEKTOR",
      artClass: "reflektor",
      previewImage: reflektorPreviewUrl,
      previewWidth: 1400,
      previewHeight: 674,
      detailPoster: reflektorDetailPosterUrl,
      href: "./projects/reflektor/",
    },
    {
      id: "bondi",
      number: "03",
      title: "Bondi",
      company: "Samsung",
      date: "2017–2019",
      artWord: "BONDI",
      artClass: "bondi",
      previewVideo: bondiRevealLoopUrl,
      previewVideoMobile: bondiRevealLoopMobileUrl,
      previewPoster: bondiRevealPosterUrl,
      previewWidth: 1920,
      previewHeight: 1080,
      detailPoster: bondiRevealPosterUrl,
      href: "./projects/bondi/",
    },
    {
      id: "vrb-foto",
      number: "04",
      title: "VRB Foto",
      company: "VRB",
      date: "2015–2017",
      artWord: "VRB FOTO",
      artClass: "vrb-foto",
      previewVideo: vrbFotoLoopUrl,
      previewVideoMobile: vrbFotoLoopMobileUrl,
      previewPoster: vrbFotoPosterUrl,
      previewWidth: 1280,
      previewHeight: 682,
      detailPoster: vrbFotoPosterUrl,
      href: "./projects/vrb-foto/",
    },
    {
      id: "definitely-games",
      number: "05",
      title: "The Core",
      company: "Definitely Games",
      date: "2026",
      titleNote: "Working title",
      artWord: "THE CORE",
      artClass: "definitely-games",
      previewImage: definitelyGamesTeaserUrl,
      previewWidth: 1800,
      previewHeight: 1008,
      status: "Work in Progress",
    },
  ];

  const index = document.getElementById("project-index");
  const detail = document.getElementById("case-study-detail");
  const revealStates = new WeakMap();
  // Keep each existing blob's starting variation stable when projects move.
  const revealShapeSeeds = new Map([
    ["horizon-worlds", 0], ["reflektor", 1], ["bondi", 2],
    ["vrb-foto", 3], ["definitely-games", 4],
  ]);
  const activeRevealRows = new Set();
  const reduceRevealMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const saveData = navigator.connection?.saveData === true;
  const canPlayProjectMedia = !reduceRevealMotion && !saveData;
  const mobileProjectMotion = window.matchMedia("(max-width: 620px), (hover: none)");
  const globalProjectReveal = window.matchMedia("(min-width: 901px) and (hover: hover)");
  const requestedEdgeBlend = new URLSearchParams(window.location.search).get("edge");
  const edgeBlendMode = ["soft", "organic", "grain"].includes(requestedEdgeBlend)
    ? requestedEdgeBlend
    : "organic";
  const visibleProjectVideos = new Map();
  const visibleProjectRows = new Set();
  const warmedProjects = new Set();
  let mobileProjectFrame = 0;
  let projectPlaybackTimer = 0;
  let previewScrollUntil = 0;
  let playingProjectVideo = null;

  function syncProjectPlayback() {
    clearTimeout(projectPlaybackTimer);
    let candidate = null;
    let largestVisibleArea = 0;
    if (heroSettled && canPlayProjectMedia && !globalProjectReveal.matches && !document.hidden) {
      visibleProjectVideos.forEach((ratio, video) => {
        if (ratio > largestVisibleArea) { candidate = video; largestVisibleArea = ratio; }
      });
    }
    if (playingProjectVideo && playingProjectVideo !== candidate) {
      playingProjectVideo.pause();
      playingProjectVideo = null;
    }
    if (!candidate || playingProjectVideo === candidate) return;
    // Warm stills while scrolling, then start the newly visible movie once the
    // swipe settles. Keep an already-playing visible preview running smoothly.
    const delay = mobileProjectMotion.matches ? previewScrollUntil - performance.now() : 0;
    if (delay > 0) {
      projectPlaybackTimer = setTimeout(syncProjectPlayback, delay);
      return;
    }
    hydrateMedia(candidate.parentElement);
    playingProjectVideo = candidate;
    candidate.play().catch(() => {
      if (playingProjectVideo === candidate) playingProjectVideo = null;
    });
  }

  heroReady.then(syncProjectPlayback);
  window.addEventListener("scroll", () => {
    if (mobileProjectMotion.matches) previewScrollUntil = performance.now() + 160;
  }, { passive: true });

  const projectVideoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
          visibleProjectVideos.set(video, entry.intersectionRatio);
        } else {
          visibleProjectVideos.delete(video);
        }

        if (entry.isIntersecting) hydrateMedia(video.parentElement, false);
      });
      syncProjectPlayback();
    },
    { threshold: [0, 0.2, 0.5, 0.8, 1] },
  );

  const projectRowObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const row = entry.target;
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.08;
        row.classList.toggle("is-in-view", isVisible);

        if (isVisible) {
          visibleProjectRows.add(row);
          if (!globalProjectReveal.matches) hydrateMedia(row, false);
        } else {
          visibleProjectRows.delete(row);
        }
      });

      requestMobileProjectMotion();
    },
    { rootMargin: "18% 0px", threshold: [0, 0.08, 0.32] },
  );

  document.addEventListener("visibilitychange", () => {
    syncProjectPlayback();
    if (document.visibilityState !== "visible") {
      index?.querySelectorAll("video").forEach((video) => video.pause());
      globalRevealLayers.forEach((layer) => setGlobalRevealLayerPlayback(layer, false));
      return;
    }

    if (canPlayProjectMedia) {
      globalRevealLayers.forEach((layer) => {
        setGlobalRevealLayerPlayback(layer, layer.isNearReveal && layer.isRevealEligible);
      });
    }
  });
  const lowPowerReveal = window.matchMedia("(update: slow)").matches
    || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
    || (navigator.deviceMemory && navigator.deviceMemory <= 4);
  const revealContourSamples = lowPowerReveal ? 72 : 96;
  const revealSearchSteps = lowPowerReveal ? 9 : 11;
  let revealFrame = 0;
  let revealLastTime = 0;
  let currentRevealRow = null;
  let globalRevealStage = null;
  let globalRevealStatus = null;
  let renderMaterial = null;
  let globalRevealLayers = [];

  if (!index) {
    return;
  }

  function escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function warmProject(row) {
    const href = row.getAttribute("href");

    if (!href || saveData || warmedProjects.has(href)) {
      return;
    }

    warmedProjects.add(href);
    const documentPrefetch = document.createElement("link");
    documentPrefetch.rel = "prefetch";
    documentPrefetch.href = href;
    document.head.append(documentPrefetch);

    const project = projects.find((item) => item.id === row.dataset.project);
    if (project?.detailPoster) {
      const imagePrefetch = document.createElement("link");
      imagePrefetch.rel = "prefetch";
      imagePrefetch.as = "image";
      imagePrefetch.href = project.detailPoster;
      document.head.append(imagePrefetch);
    }
  }

  function renderMobileProjectMotion() {
    mobileProjectFrame = 0;

    if (!mobileProjectMotion.matches || reduceRevealMotion) {
      visibleProjectRows.forEach((row) => row.style.removeProperty("--mobile-media-y"));
      return;
    }

    const viewportCenter = window.innerHeight * 0.5;
    visibleProjectRows.forEach((row) => {
      const rect = row.getBoundingClientRect();
      const distance = rect.top + rect.height * 0.5 - viewportCenter;
      const shift = clamp(distance / Math.max(window.innerHeight, 1), -1, 1) * -12;
      row.style.setProperty("--mobile-media-y", `${shift.toFixed(2)}px`);
    });
  }

  function requestMobileProjectMotion() {
    if (!mobileProjectFrame) {
      mobileProjectFrame = window.requestAnimationFrame(renderMobileProjectMotion);
    }
  }

  function makeGlobalRevealMedia(project, feather = false) {
    const artClass = project.artClass || project.id;

    if (!feather && project.previewVideo && canPlayProjectMedia) {
      const video = document.createElement("video");
      video.className = `project-global-media project-global-media--${artClass}`;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "none";
      video.dataset.poster = project.previewPoster;
      video.width = project.previewWidth;
      video.height = project.previewHeight;
      video.setAttribute("aria-hidden", "true");

      const source = document.createElement("source");
      source.dataset.src = project.previewVideo;
      source.type = "video/mp4";
      video.append(source);
      return video;
    }

    const image = document.createElement("img");
    image.className = `project-global-media project-global-media--${artClass}`;
    image.dataset.src = project.previewImage || project.previewPoster;
    image.alt = "";
    image.width = project.previewWidth;
    image.height = project.previewHeight;
    image.decoding = "async";
    image.setAttribute("aria-hidden", "true");
    return image;
  }

  function createProjectEdgeMasks(mode) {
    return revealMasks[mode] || revealMasks.organic;
  }

  function hydrateMedia(container, loadVideo = true) {
    const changedVideos = new Set();
    container.querySelectorAll("[data-src], [data-poster]").forEach((media) => {
      if (media.dataset.poster) {
        media.poster = media.dataset.poster;
        delete media.dataset.poster;
      }
      if (media.dataset.src && (loadVideo || media.tagName !== "SOURCE")) {
        media.src = media.dataset.src;
        delete media.dataset.src;
        if (media.tagName === "SOURCE") changedVideos.add(media.closest("video"));
      }
    });
    changedVideos.forEach((video) => video?.load());
  }

  function setGlobalRevealLayerPlayback(layer, shouldPlay) {
    if (shouldPlay) {
      hydrateMedia(layer.sharp);
      hydrateMedia(layer.feather);
    }
    const video = layer?.sharp.querySelector("video");
    if (!video) return;

    if (shouldPlay && canPlayProjectMedia && document.visibilityState === "visible") {
      if (video.dataset.revealPlaying !== "true") {
        video.dataset.revealPlaying = "true";
        video.play().catch(() => {
          delete video.dataset.revealPlaying;
        });
      }
    } else {
      video.pause();
      delete video.dataset.revealPlaying;
    }
  }

  function hideGlobalReveal() {
    globalRevealStage?.classList.remove("is-visible");
    globalRevealLayers.forEach((layer) => {
      layer.isNearReveal = false;
      layer.isRevealEligible = false;
      setGlobalRevealLayerPlayback(layer, false);
    });
  }

  function transitionGlobalReveal() {
    if (!globalProjectReveal.matches || !globalRevealStage) return;
    globalRevealStage.classList.add("is-visible");
  }

  function createGlobalRevealStage() {
    index.insertAdjacentHTML(
      "beforeend",
      `
        <span class="project-global-reveal" data-edge-blend="${edgeBlendMode}" aria-hidden="true">
          <span class="project-global-feather">
            <span class="project-global-feather-clip"></span>
          </span>
          <span class="project-global-shell">
            <span class="project-global-texture"></span>
            <span class="project-art-status project-global-status" hidden></span>
          </span>
        </span>
      `,
    );

    globalRevealStage = index.querySelector(".project-global-reveal");
    renderMaterial = attachBlobInset(globalRevealStage);
    globalRevealStatus = globalRevealStage.querySelector(".project-global-status");
    const edgeMasks = createProjectEdgeMasks(edgeBlendMode);
    const sharpShell = globalRevealStage.querySelector(".project-global-shell");
    const featherShell = globalRevealStage.querySelector(".project-global-feather-clip");
    const rows = Array.from(index.querySelectorAll(".project-row"));
    globalRevealStage.style.setProperty("--project-edge-mask-top", `url(${edgeMasks.top})`);
    globalRevealStage.style.setProperty("--project-edge-mask-bottom", `url(${edgeMasks.bottom})`);
    globalRevealStage.style.setProperty("--project-edge-mask-both", `url(${edgeMasks.both})`);

    globalRevealLayers = projects.map((project, projectIndex) => {
      const sharp = document.createElement("span");
      const feather = document.createElement("span");
      const layerClass = `project-global-layer project-global-layer--${project.artClass || project.id}`;
      sharp.className = layerClass;
      feather.className = layerClass;
      if (projectIndex === 0) {
        sharp.classList.add("is-first");
        feather.classList.add("is-first");
      }
      if (projectIndex === projects.length - 1) {
        sharp.classList.add("is-last");
        feather.classList.add("is-last");
      }
      sharp.style.setProperty("--project-layer-index", projectIndex);
      feather.style.setProperty("--project-layer-index", projectIndex);
      sharp.append(makeGlobalRevealMedia(project));
      feather.append(makeGlobalRevealMedia(project, true));
      sharpShell.insertBefore(sharp, sharpShell.querySelector(".project-global-texture"));
      featherShell.append(feather);

      return {
        sharp,
        feather,
        row: rows[projectIndex],
        isFirst: projectIndex === 0,
        isNearReveal: false,
        isRevealEligible: false,
      };
    });
  }

  function renderProjectIndex(activeId) {
    projectVideoObserver.disconnect();
    projectRowObserver.disconnect();
    visibleProjectVideos.clear();
    syncProjectPlayback();
    visibleProjectRows.clear();
    index.innerHTML = projects
      .map(
        (project) => {
          const isWorkInProgress = Boolean(project.status);
          const isComingSoon = !project.href;
          const tagName = project.href ? "a" : "button";
          const href = project.href ? ` href="${escapeHtml(project.href)}"` : "";
          const buttonType = isComingSoon ? ' type="button"' : "";
          const comingSoon = isComingSoon ? ' data-coming-soon' : "";
          const accessibilityLabel = isComingSoon
            ? ` aria-label="${escapeHtml(`${project.title}, ${project.titleNote ? `${project.titleNote}, ` : ""}${project.company}, ${project.date}.${project.status ? ` ${project.status}.` : ""} Case study coming soon.`)}"`
            : "";
          const artClass = project.artClass || project.id;
          const hasPreviewMedia = Boolean(project.previewVideo || project.previewImage);
          const mediaMarkup = project.previewVideo
            ? `
                <video
                  class="project-art-media"
                  muted
                  loop
                  playsinline
                  preload="none"
                  data-poster="${escapeHtml(project.previewPoster)}"
                  width="${project.previewWidth}"
                  height="${project.previewHeight}"
                  aria-hidden="true"
                >
                  ${project.previewVideoMobile ? `<source media="(max-width: 620px)" data-src="${escapeHtml(project.previewVideoMobile)}" type="video/mp4">` : ""}
                  <source data-src="${escapeHtml(project.previewVideo)}" type="video/mp4">
                </video>
              `
            : project.previewImage
              ? `<img class="project-art-media" data-src="${escapeHtml(project.previewImage)}" alt="" width="${project.previewWidth}" height="${project.previewHeight}" loading="lazy" decoding="async" fetchpriority="low" aria-hidden="true">`
              : "";
          const previewStatus = project.status || (isComingSoon ? "Coming soon" : "");
          const statusMarkup = previewStatus
            ? `<span class="project-art-status">${escapeHtml(previewStatus)}</span>`
            : "";

          return `
          <span
            id="${escapeHtml(project.id)}"
            class="project-anchor-marker"
            aria-hidden="true"
          ></span>
          <${tagName}
            class="project-row${isWorkInProgress ? " project-row--wip" : ""}${isComingSoon ? " project-row--pending" : ""}"
            data-project="${project.id}"
            data-preview-status="${escapeHtml(previewStatus)}"
            ${buttonType}${comingSoon}${accessibilityLabel}
            ${href}
          >
            <span class="project-copy">
              <span class="project-meta">
                <span class="project-company">${escapeHtml(project.company)}</span>
                <span class="project-date">${escapeHtml(project.date)}</span>
              </span>
              <span class="project-title${project.titleNote ? " project-title--noted" : ""}">
                <span>${escapeHtml(project.title)}</span>
                ${project.titleNote ? `<span class="project-title-note">${escapeHtml(project.titleNote)}</span>` : ""}
              </span>
            </span>
            <span class="project-peek" aria-hidden="true">
              <span class="project-art project-art--${artClass}${hasPreviewMedia ? " project-art--media" : ""}">
                ${mediaMarkup}
                <span class="project-art-number">${project.number}</span>
                <span class="project-art-word">${escapeHtml(project.artWord)}</span>
                <span class="project-art-cut project-art-cut--a"></span>
                <span class="project-art-cut project-art-cut--b"></span>
                <span class="project-art-cut project-art-cut--c"></span>
                ${statusMarkup}
              </span>
            </span>
          </${tagName}>
        `;
        },
      )
      .join("");

    createGlobalRevealStage();

    if (!globalProjectReveal.matches) {
      index.querySelectorAll("video.project-art-media").forEach((video) => {
        projectVideoObserver.observe(video);
      });
    }
    index.querySelectorAll(".project-row").forEach((row) => projectRowObserver.observe(row));
    requestMobileProjectMotion();
  }

  globalProjectReveal.addEventListener("change", () => {
    projectVideoObserver.disconnect();
    visibleProjectVideos.clear();
    index.querySelectorAll("video.project-art-media").forEach((video) => {
      video.pause();
      if (!globalProjectReveal.matches) projectVideoObserver.observe(video);
    });
    if (!globalProjectReveal.matches) {
      hideGlobalReveal();
      visibleProjectRows.forEach((row) => hydrateMedia(row, false));
    }
    syncProjectPlayback();
    requestMobileProjectMotion();
  });

  function renderCaseStudy(project) {
    detail.innerHTML = `
      <div class="case-hero">
        <div>
          <p class="section-kicker">${project.number} / Selected project</p>
          <h3>${escapeHtml(project.title)}</h3>
          <p class="case-summary">${escapeHtml(project.challenge)}</p>
        </div>
        <div class="case-meta">
          <div class="meta-item">
            <span>Role</span>
            <p>${escapeHtml(project.role)}</p>
          </div>
          <div class="meta-item">
            <span>Context</span>
            <p>${escapeHtml(project.context)}</p>
          </div>
          <div class="meta-item">
            <span>Outcome</span>
            <p>${escapeHtml(project.outcome)}</p>
          </div>
        </div>
      </div>
      <div class="case-grid">
        ${project.moves
          .map(
            (move, index) => `
              <section class="case-card">
                <span>${String(index + 1).padStart(2, "0")}</span>
                <h4>${["Frame", "Make", "Move"][index]}</h4>
                <p>${escapeHtml(move)}</p>
              </section>
            `,
          )
          .join("")}
      </div>
      <div class="artifact-strip" aria-label="Suggested artifacts">
        ${project.artifacts
          .map(
            (artifact) => `
              <div class="artifact">
                <strong>Artifact</strong>
                <p>${escapeHtml(artifact)}</p>
              </div>
            `,
          )
          .join("")}
      </div>
    `;
  }

  function activateProject(projectId, shouldScroll = false) {
    const project = projects.find((item) => item.id === projectId) || projects[0];
    renderProjectIndex(project.id);
    renderCaseStudy(project);

    if (shouldScroll) {
      detail.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function smoothRange(edgeA, edgeB, value) {
    const amount = clamp((value - edgeA) / Math.max(edgeB - edgeA, 0.0001), 0, 1);
    return amount * amount * (3 - 2 * amount);
  }

  function getRevealState(row, x, y) {
    let state = revealStates.get(row);

    if (!state) {
      const projectIndex = Array.from(index.querySelectorAll(".project-row")).indexOf(row);
      const shapeSeed = revealShapeSeeds.get(row.dataset.project) ?? projectIndex;
      state = {
        x,
        y,
        targetX: x,
        targetY: y,
        velocityX: 0,
        velocityY: 0,
        pointerX: 0,
        pointerY: 0,
        deformX: 0,
        deformY: 0,
        surfaceX: 0,
        surfaceY: 0,
        artX: 0,
        artY: 0,
        lastPointerX: x,
        lastPointerY: y,
        open: 0,
        openVelocity: 0,
        targetOpen: 0,
        closing: false,
        closeStartOpen: 1,
        closeElapsed: 0,
        closeDuration: 0.48,
        closeAngle: 0,
        closeDriftX: 0,
        closeDriftY: 0,
        closeSplit: false,
        closeBallRates: [1, 1, 1, 1, 1],
        phase: (shapeSeed + 1) * 0.173,
        life: shapeSeed * 1.31,
        energy: 0,
        clickPulse: 0,
        clickLife: 0,
        frameDelta: 0,
        metaballs: [],
      };
      revealStates.set(row, state);
    }

    return state;
  }

  function handoffRevealState(sourceRow, targetRow, targetX, targetY) {
    const sourceState = revealStates.get(sourceRow);

    if (!sourceState) {
      return getRevealState(targetRow, targetX, targetY);
    }

    const sourceRect = sourceRow.getBoundingClientRect();
    const targetRect = targetRow.getBoundingClientRect();
    const state = getRevealState(
      targetRow,
      sourceRect.left + sourceState.x - targetRect.left,
      sourceRect.top + sourceState.y - targetRect.top,
    );

    state.x = sourceRect.left + sourceState.x - targetRect.left;
    state.y = sourceRect.top + sourceState.y - targetRect.top;
    state.targetX = targetX;
    state.targetY = targetY;
    state.velocityX = sourceState.velocityX;
    state.velocityY = sourceState.velocityY;
    state.pointerX = sourceState.pointerX;
    state.pointerY = sourceState.pointerY;
    state.deformX = sourceState.deformX;
    state.deformY = sourceState.deformY;
    state.surfaceX = sourceState.surfaceX;
    state.surfaceY = sourceState.surfaceY;
    state.artX = sourceState.artX;
    state.artY = sourceState.artY;
    state.lastPointerX = targetX;
    state.lastPointerY = targetY;
    state.open = Math.max(sourceState.open, 0.92);
    state.openVelocity = sourceState.openVelocity;
    state.targetOpen = 1;
    state.closing = false;
    state.closeElapsed = 0;
    state.phase = sourceState.phase;
    state.life = sourceState.life;
    state.energy = sourceState.energy;
    state.clickPulse = sourceState.clickPulse;
    state.clickLife = sourceState.clickLife;
    state.frameDelta = sourceState.frameDelta;
    state.metaballs = sourceState.metaballs.map((ball) => ({ ...ball }));

    return state;
  }

  function positionGlobalRevealLayer(layer, stageLeft, stageTop, stageHeight, artX, artY, pointerY, coverage) {
    if (!layer?.row) return;

    const rect = layer.row.getBoundingClientRect();
    const blendDepth = clamp(rect.height * 0.28, 70, 104);
    const edgeGuard = 18;
    const mediaWidth = Math.max(rect.width, coverage.right - coverage.left + edgeGuard * 2);
    const mediaHeight = Math.max(rect.height + blendDepth * 2, coverage.bottom - coverage.top + edgeGuard * 2);
    // Keep the artwork behind the entire contour, including its lagging tail and feather.
    const mediaX = clamp(
      rect.left - stageLeft + artX,
      coverage.right + edgeGuard - mediaWidth,
      coverage.left - edgeGuard,
    );
    const mediaY = clamp(
      rect.top - blendDepth - stageTop + artY,
      coverage.bottom + edgeGuard - mediaHeight,
      coverage.top - edgeGuard,
    );
    const isNearReveal = rect.bottom + blendDepth > stageTop
      && rect.top - blendDepth < stageTop + stageHeight;
    // Anchor the handoff to the shared boundary so switching rows cannot toggle the artwork.
    const opacity = layer.isFirst ? 1 : smoothRange(
      rect.top - blendDepth * 0.12,
      rect.top + blendDepth * 0.9,
      pointerY,
    );

    [layer.sharp, layer.feather].forEach((element) => {
      element.style.setProperty("--project-layer-opacity", opacity.toFixed(4));
      element.style.setProperty("--project-edge-fill", opacity.toFixed(4));
      element.style.setProperty("--reveal-media-width", `${mediaWidth.toFixed(2)}px`);
      element.style.setProperty("--reveal-media-height", `${mediaHeight.toFixed(2)}px`);
      element.style.setProperty("--reveal-media-x", `${mediaX.toFixed(2)}px`);
      element.style.setProperty("--reveal-media-y", `${mediaY.toFixed(2)}px`);
    });

    layer.isNearReveal = isNearReveal;
    return opacity;
  }

  function renderGlobalReveal(row, state, points, rect) {
    if (!globalProjectReveal.matches || !globalRevealStage || currentRevealRow !== row) return;

    const previewStatus = row.dataset.previewStatus || "";
    if (globalRevealStatus.textContent !== previewStatus) {
      globalRevealStatus.textContent = previewStatus;
    }
    globalRevealStatus.hidden = !previewStatus;

    const stageWidth = clamp(rect.width * 0.64, 680, 860);
    const stageHeight = clamp(rect.height * 1.34, 420, 520);
    const centerX = rect.left + state.x;
    const centerY = rect.top + state.y;
    const stageLeft = centerX - stageWidth * 0.5;
    const stageTop = centerY - stageHeight * 0.5;
    const coverage = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
    const clip = `polygon(${points.map((point) => {
      const x = rect.left + point.x - stageLeft;
      const y = rect.top + point.y - stageTop;
      coverage.left = Math.min(coverage.left, x);
      coverage.right = Math.max(coverage.right, x);
      coverage.top = Math.min(coverage.top, y);
      coverage.bottom = Math.max(coverage.bottom, y);
      return `${x.toFixed(1)}px ${y.toFixed(1)}px`;
    }).join(",")})`;

    globalRevealStage.style.width = `${stageWidth.toFixed(2)}px`;
    globalRevealStage.style.height = `${stageHeight.toFixed(2)}px`;
    globalRevealStage.style.transform = `translate3d(${stageLeft.toFixed(2)}px, ${stageTop.toFixed(2)}px, 0)`;
    globalRevealStage.style.setProperty("--global-reveal-clip", clip);
    renderMaterial?.(points, state, rect, stageLeft, stageTop, stageWidth, stageHeight);
    const closeProgress = state.closing
      ? 1 - clamp(state.open / Math.max(state.closeStartOpen, 0.001), 0, 1)
      : 0;
    const revealOpacity = state.closing
      ? 1 - smoothRange(0.76, 0.98, closeProgress)
      : 1;
    globalRevealStage.style.setProperty("--global-reveal-opacity", revealOpacity.toFixed(3));
    globalRevealStage.classList.add("is-visible");
    const pointerY = rect.top + state.targetY;

    const layerOpacities = globalRevealLayers.map((layer) => (
      positionGlobalRevealLayer(
        layer,
        stageLeft,
        stageTop,
        stageHeight,
        state.artX,
        state.artY,
        pointerY,
        coverage,
      )
    ));
    const baseLayerIndex = layerOpacities.reduce(
      (baseIndex, opacity, layerIndex) => opacity === 1 ? layerIndex : baseIndex,
      0,
    );

    globalRevealLayers.forEach((layer, layerIndex) => {
      const isVisible = layerIndex >= baseLayerIndex && layerOpacities[layerIndex] > 0;
      layer.sharp.style.visibility = isVisible ? "visible" : "hidden";
      layer.feather.style.visibility = isVisible ? "visible" : "hidden";
      layer.isRevealEligible = isVisible;
      setGlobalRevealLayerPlayback(layer, layer.isNearReveal && isVisible);
    });
  }

  function makeWaveReveal(row, state) {
    const rect = row.getBoundingClientRect();

    if (rect.width < 1 || rect.height < 1) {
      return;
    }

    const open = state.open * state.open * (3 - 2 * state.open);
    const isClosing = state.closing && state.targetOpen === 0;
    const closeProgress = isClosing
      ? 1 - clamp(state.open / Math.max(state.closeStartOpen, 0.001), 0, 1)
      : 0;
    const closeEase = smoothRange(0.04, 0.96, closeProgress);
    const cursorX = state.targetX / rect.width - 0.5;
    const cursorY = state.targetY / rect.height - 0.5;
    const speed = Math.hypot(state.deformX, state.deformY);
    const breath = Math.sin((state.life * 0.42 + state.phase) * Math.PI * 2);
    const baseSpreadX = clamp(rect.width * 0.18, 190, 280);
    const baseSpreadY = clamp(rect.height * 0.27, 86, 122);
    const openScale = isClosing ? Math.max(0.008, open) : 0.12 + open * 0.88;
    const spreadX = baseSpreadX * openScale;
    const spreadY = baseSpreadY * openScale;
    const internalX = clamp(state.surfaceX, -spreadX * 0.98, spreadX * 0.98);
    const internalY = clamp(state.surfaceY, -spreadY * 0.98, spreadY * 0.98);
    const edgePressure = clamp(
      Math.hypot(internalX / (spreadX * 0.68), internalY / (spreadY * 0.7)),
      0,
      1.4,
    );
    const motionX = clamp(state.deformX * 1.8 + state.velocityX * 1.15, -92, 92) * openScale;
    const motionY = clamp(state.deformY * 1.8 + state.velocityY * 1.15, -72, 72) * openScale;
    const quietDrift = 15 * openScale;
    const ballTargets = [
      {
        x: -spreadX * 0.52 + internalX * 0.12
          + Math.sin(state.life * 0.72 + state.phase * 9) * quietDrift,
        y: internalY * 0.1
          + Math.sin(state.life * 0.57 + state.phase * 5) * quietDrift * 0.72,
        radius: spreadY * (0.76 + breath * 0.025),
      },
      {
        x: spreadX * 0.5 + internalX * 0.12
          + Math.cos(state.life * 0.61 + state.phase * 7) * quietDrift,
        y: internalY * 0.1
          + Math.cos(state.life * 0.49 + state.phase * 11) * quietDrift * 0.76,
        radius: spreadY * (0.78 - breath * 0.022),
      },
      {
        x: internalX * 0.22 + Math.sin(state.life * 0.43) * quietDrift * 0.55,
        y: -spreadY * 0.22 + internalY * 0.2,
        radius: spreadY * (0.9 + Math.sin(state.life * 0.38) * 0.025),
      },
      {
        x: -motionX * 0.62 + internalX * 0.1
          + Math.cos(state.life * 0.66 + 1.4) * quietDrift,
        y: spreadY * 0.34 - motionY * 0.62 + internalY * 0.1,
        radius: spreadY * (0.65 + state.energy * 0.12),
      },
      {
        x: internalX * 0.94 + motionX * 0.18
          + Math.sin(state.life * 0.79 + 2.1) * quietDrift * 0.7,
        y: internalY * 0.94 + motionY * 0.18,
        radius: spreadY * (0.5 + state.energy * 0.1 + edgePressure * 0.11),
      },
    ];
    const clickWobble = Math.sin(state.clickLife * 22) * state.clickPulse * spreadY * 0.14;

    ballTargets.forEach((target, targetIndex) => {
      const direction = targetIndex % 2 === 0 ? 1 : -1;
      target.x += clickWobble * direction;
      target.y += clickWobble * 0.32 * (targetIndex % 3 - 1);

      if (isClosing) {
        const drift = closeEase * (0.72 + targetIndex * 0.055);
        const orbit = Math.sin(state.closeAngle * 2.3 + targetIndex * 1.71)
          * baseSpreadY * 0.075 * closeEase;
        target.x += state.closeDriftX * drift - Math.sin(state.closeAngle) * orbit;
        target.y += state.closeDriftY * drift + Math.cos(state.closeAngle) * orbit;
        target.radius *= Math.max(
          0.16,
          1 - closeEase * 0.58 * state.closeBallRates[targetIndex],
        );

        if (state.closeSplit) {
          const splitEnvelope = smoothRange(0.1, 0.42, closeProgress)
            * (1 - smoothRange(0.9, 1, closeProgress));
          const splitDirection = targetIndex === 0 || targetIndex === 2 || targetIndex === 3
            ? -1
            : 1;
          const splitDistance = baseSpreadY * 0.62 * splitEnvelope * splitDirection;
          const splitAngle = state.closeAngle + Math.PI * 0.5;
          target.x += Math.cos(splitAngle) * splitDistance;
          target.y += Math.sin(splitAngle) * splitDistance;
        }
      }
    });
    const delta = state.frameDelta || 1 / 60;

    if (state.metaballs.length !== ballTargets.length) {
      state.metaballs = ballTargets.map((target) => ({
        x: target.x,
        y: target.y,
        radius: target.radius,
        velocityX: 0,
        velocityY: 0,
      }));
    }

    state.metaballs.forEach((ball, ballIndex) => {
      const target = ballTargets[ballIndex];
      const isPointerMass = ballIndex === ballTargets.length - 1;
      const stiffness = isPointerMass ? 176 : 42 + ballIndex * 7;
      const damping = isPointerMass ? 19 : 10.8 + ballIndex * 0.8;
      ball.velocityX += (target.x - ball.x) * stiffness * delta;
      ball.velocityY += (target.y - ball.y) * stiffness * delta;
      const damp = Math.exp(-damping * delta);
      ball.velocityX *= damp;
      ball.velocityY *= damp;
      ball.x += ball.velocityX * delta;
      ball.y += ball.velocityY * delta;
      ball.radius += (target.radius - ball.radius)
        * (1 - Math.exp(-(isPointerMass ? 17 : 8.5) * delta));
    });

    const angle = clamp(
      internalY / Math.max(spreadY, 1) * -0.055 + state.deformX * 0.0014,
      -0.1,
      0.1,
    );
    const angleCos = Math.cos(angle);
    const angleSin = Math.sin(angle);
    const pulseTime = state.life * 1.18 + state.phase * 4;
    const pulseAmount = (2.8 + state.energy * 4.2) * open;
    const pulseScale = 0.017;
    const threshold = 1.42 - state.energy * 0.08 - Math.min(edgePressure, 1) * 0.05;
    const maxBallRadius = Math.max(...state.metaballs.map((ball) => ball.radius));
    const maxRadius = Math.hypot(spreadX * 1.25, spreadY * 1.2) + maxBallRadius * 2.7 + 70;
    const points = [];

    function fieldAt(x, y) {
      const waveX = Math.sin(y * pulseScale + pulseTime)
        + 0.34 * Math.sin(y * pulseScale * 2.1 - pulseTime * 1.45);
      const waveY = Math.sin(x * pulseScale - pulseTime * 1.05)
        + 0.34 * Math.sin(x * pulseScale * 1.9 + pulseTime * 1.2);
      const sampleX = x + waveX * pulseAmount;
      const sampleY = y + waveY * pulseAmount;

      return state.metaballs.reduce((sum, ball) => {
        const dx = sampleX - ball.x;
        const dy = sampleY - ball.y;
        return sum + ball.radius * ball.radius / (dx * dx + dy * dy + 1);
      }, 0);
    }

    for (let pointIndex = 0; pointIndex < revealContourSamples; pointIndex += 1) {
      const theta = pointIndex / revealContourSamples * Math.PI * 2;
      const rayX = Math.cos(theta);
      const rayY = Math.sin(theta);
      let insideRadius = 0;
      let outsideRadius = maxRadius;

      for (let searchStep = 0; searchStep < revealSearchSteps; searchStep += 1) {
        const radius = (insideRadius + outsideRadius) * 0.5;

        if (fieldAt(rayX * radius, rayY * radius) >= threshold) {
          insideRadius = radius;
        } else {
          outsideRadius = radius;
        }
      }

      const contourRadius = (insideRadius + outsideRadius) * 0.5;
      const localX = rayX * contourRadius;
      const localY = rayY * contourRadius;

      points.push({
        x: state.x + localX * angleCos - localY * angleSin,
        y: state.y + localX * angleSin + localY * angleCos,
      });
    }

    if (globalProjectReveal.matches) {
      renderGlobalReveal(row, state, points, rect);
    } else {
      row.style.setProperty(
        "--reveal-clip",
        `polygon(${points.map((point) => `${point.x.toFixed(1)}px ${point.y.toFixed(1)}px`).join(",")})`,
      );
      row.style.setProperty("--reveal-center-x", `${state.x.toFixed(2)}px`);
      row.style.setProperty("--reveal-center-y", `${state.y.toFixed(2)}px`);

      if (speed > 0.01) {
        row.style.setProperty("--reveal-speed", Math.min(speed / 36, 1).toFixed(3));
      }

      row.style.setProperty("--reveal-art-x", `${state.artX.toFixed(2)}px`);
      row.style.setProperty("--reveal-art-y", `${state.artY.toFixed(2)}px`);
    }
  }

  function requestRevealFrame() {
    if (!revealFrame) {
      revealFrame = window.requestAnimationFrame(updateRevealFrame);
    }
  }

  function getBlobFollowTarget(row, state) {
    const rect = row.getBoundingClientRect();
    const slackX = clamp(rect.width * 0.18, 190, 280) * 0.62;
    const slackY = clamp(rect.height * 0.27, 86, 122) * 0.62;
    const offsetX = state.targetX - state.x;
    const offsetY = state.targetY - state.y;
    const tension = Math.hypot(offsetX / slackX, offsetY / slackY);

    if (tension <= 1) {
      return { x: state.x, y: state.y, tension };
    }

    return {
      x: state.targetX - offsetX / tension,
      y: state.targetY - offsetY / tension,
      tension,
    };
  }

  function updateRevealFrame(timestamp) {
    revealFrame = 0;
    const delta = revealLastTime ? clamp((timestamp - revealLastTime) / 1000, 0, 0.05) : 0;
    revealLastTime = timestamp;
    let shouldContinue = false;

    activeRevealRows.forEach((row) => {
      const state = revealStates.get(row);

      if (!state || !row.isConnected) {
        activeRevealRows.delete(row);
        return;
      }

      const followTarget = getBlobFollowTarget(row, state);

      if (reduceRevealMotion) {
        state.x = state.targetX;
        state.y = state.targetY;
        state.surfaceX = 0;
        state.surfaceY = 0;
        state.artX = 0;
        state.artY = 0;
        state.open = state.targetOpen;
        state.velocityX = 0;
        state.velocityY = 0;
        state.openVelocity = 0;
      } else {
        state.velocityX = (state.velocityX + (followTarget.x - state.x) * 0.12) * 0.74;
        state.velocityY = (state.velocityY + (followTarget.y - state.y) * 0.12) * 0.74;
        state.x += state.velocityX;
        state.y += state.velocityY;
        if (state.closing) {
          state.closeElapsed += delta;
          const closeProgress = clamp(state.closeElapsed / state.closeDuration, 0, 1);
          state.open = state.closeStartOpen * (1 - smoothRange(0, 1, closeProgress));
          state.openVelocity = 0;
        } else {
          state.openVelocity = (state.openVelocity + (state.targetOpen - state.open) * 0.15) * 0.7;
          state.open = clamp(state.open + state.openVelocity, 0, 1.04);
        }

        const rect = row.getBoundingClientRect();
        const surfaceTargetX = clamp(
          state.targetX - state.x + state.deformX * 0.38,
          -clamp(rect.width * 0.18, 190, 280) * 0.98,
          clamp(rect.width * 0.18, 190, 280) * 0.98,
        );
        const surfaceTargetY = clamp(
          state.targetY - state.y + state.deformY * 0.38,
          -clamp(rect.height * 0.27, 86, 122) * 0.98,
          clamp(rect.height * 0.27, 86, 122) * 0.98,
        );
        const surfaceEase = 1 - Math.exp(-28 * delta);
        const artEase = 1 - Math.exp(-9 * delta);
        const artTargetX = -(state.targetX / Math.max(rect.width, 1) - 0.5) * 9 * state.open;
        const artTargetY = -(state.targetY / Math.max(rect.height, 1) - 0.5) * 6 * state.open;

        state.surfaceX += (surfaceTargetX - state.surfaceX) * surfaceEase;
        state.surfaceY += (surfaceTargetY - state.surfaceY) * surfaceEase;
        state.artX += (artTargetX - state.artX) * artEase;
        state.artY += (artTargetY - state.artY) * artEase;
      }

      state.life += delta * (1 + state.energy * 0.52);
      state.clickLife += delta;
      state.clickPulse *= Math.exp(-delta * 4.4);
      state.frameDelta = delta;
      state.energy *= Math.exp(-delta * 2.25);
      state.deformX += (state.pointerX - state.deformX) * 0.2;
      state.deformY += (state.pointerY - state.deformY) * 0.2;
      state.pointerX *= 0.76;
      state.pointerY *= 0.76;
      makeWaveReveal(row, state);

      const stillMoving = Math.abs(followTarget.x - state.x) > 0.08
        || Math.abs(followTarget.y - state.y) > 0.08
        || Math.abs(state.velocityX) > 0.08
        || Math.abs(state.velocityY) > 0.08
        || Math.abs(state.targetOpen - state.open) > 0.004
        || Math.abs(state.openVelocity) > 0.004
        || Math.abs(state.deformX) > 0.08
        || Math.abs(state.deformY) > 0.08
        || state.clickPulse > 0.01;

      if (state.targetOpen === 0 && state.open < 0.006 && !stillMoving) {
        activeRevealRows.delete(row);
        row.classList.remove("is-reveal-active");

        if (currentRevealRow === row) {
          currentRevealRow = null;
          hideGlobalReveal();
        }
      } else {
        shouldContinue = shouldContinue
          || stillMoving
          || (state.targetOpen > 0 && !reduceRevealMotion);
      }
    });

    if (shouldContinue) {
      requestRevealFrame();
    } else {
      revealLastTime = 0;
    }
  }

  function openReveal(row, x, y, sourceRow = null) {
    const state = sourceRow && sourceRow !== row
      ? handoffRevealState(sourceRow, row, x, y)
      : getRevealState(row, x, y);
    state.targetX = x;
    state.targetY = y;
    state.targetOpen = 1;
    state.closing = false;
    state.closeElapsed = 0;
    globalRevealStage?.style.setProperty("--global-reveal-opacity", "1");
    row.classList.add("is-reveal-active");
    activeRevealRows.add(row);
    makeWaveReveal(row, state);
    requestRevealFrame();
    return state;
  }

  function closeReveal(row) {
    const state = revealStates.get(row);

    if (!state || state.closing) {
      return;
    }

    const motion = Math.hypot(state.deformX, state.deformY);
    const fallbackAngle = Math.random() * Math.PI * 2;
    state.closeAngle = motion > 1.5
      ? Math.atan2(state.deformY, state.deformX) + (Math.random() - 0.5) * 0.8
      : fallbackAngle;
    const driftDistance = 12 + Math.random() * 26;
    state.closeDriftX = Math.cos(state.closeAngle) * driftDistance;
    state.closeDriftY = Math.sin(state.closeAngle) * driftDistance;
    state.closeStartOpen = Math.max(state.open, 0.001);
    state.closeElapsed = 0;
    state.closeDuration = (0.4 + Math.random() * 0.14)
      * (0.65 + 0.35 * clamp(state.open, 0, 1));
    state.closeSplit = Math.random() < 0.4;
    state.closeBallRates = Array.from({ length: 5 }, () => 0.72 + Math.random() * 0.63);
    state.closing = true;
    state.targetOpen = 0;
    row.classList.remove("is-reveal-active");
    activeRevealRows.add(row);
    requestRevealFrame();
  }

  index.addEventListener("pointerover", (event) => {
    if (event.pointerType === "touch") {
      return;
    }

    const row = event.target.closest(".project-row");

    if (!row || row.contains(event.relatedTarget)) {
      return;
    }

    warmProject(row);

    const rect = row.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const sourceRow = event.relatedTarget?.closest?.(".project-row") || currentRevealRow;
    currentRevealRow = row;
    transitionGlobalReveal();
    const state = openReveal(row, x, y, sourceRow);
    state.lastPointerX = x;
    state.lastPointerY = y;
  });

  index.addEventListener("pointermove", (event) => {
    if (event.pointerType === "touch") {
      return;
    }

    const row = event.target.closest(".project-row");

    if (!row) {
      return;
    }

    const rect = row.getBoundingClientRect();
    const x = clamp(event.clientX - rect.left, 0, rect.width);
    const y = clamp(event.clientY - rect.top, 0, rect.height);
    const state = getRevealState(row, x, y);
    const pointerDeltaX = x - state.lastPointerX;
    const pointerDeltaY = y - state.lastPointerY;

    state.targetX = x;
    state.targetY = y;
    state.targetOpen = 1;
    state.pointerX = clamp(pointerDeltaX, -42, 42);
    state.pointerY = clamp(pointerDeltaY, -42, 42);
    state.lastPointerX = x;
    state.lastPointerY = y;
    const interiorOffset = Math.hypot(state.targetX - state.x, state.targetY - state.y);
    state.energy = Math.max(
      state.energy,
      clamp(Math.hypot(state.pointerX, state.pointerY) / 30 + interiorOffset / 620, 0, 1),
    );
    activeRevealRows.add(row);
    currentRevealRow = row;
    requestRevealFrame();
  });

  index.addEventListener("pointerout", (event) => {
    if (event.pointerType === "touch") {
      return;
    }

    const row = event.target.closest(".project-row");

    if (!row || row.contains(event.relatedTarget)) {
      return;
    }

    closeReveal(row);

  });

  index.addEventListener("focusin", (event) => {
    const row = event.target.closest(".project-row");

    if (!row || !row.matches(":focus-visible")) {
      return;
    }

    warmProject(row);

    const rect = row.getBoundingClientRect();
    const sourceRow = currentRevealRow;
    currentRevealRow = row;
    transitionGlobalReveal();
    openReveal(row, rect.width * 0.72, rect.height * 0.5);
  });

  index.addEventListener("focusout", (event) => {
    const row = event.target.closest(".project-row");

    if (row && !row.contains(event.relatedTarget)) {
      closeReveal(row);
    }
  });

  index.addEventListener("click", (event) => {
    const row = event.target.closest("[data-coming-soon]");

    if (!row) {
      return;
    }

    event.preventDefault();
    const rect = row.getBoundingClientRect();
    const hasPointerPosition = event.clientX > 0 || event.clientY > 0;
    const x = hasPointerPosition
      ? clamp(event.clientX - rect.left, 0, rect.width)
      : rect.width * 0.68;
    const y = hasPointerPosition
      ? clamp(event.clientY - rect.top, 0, rect.height)
      : rect.height * 0.5;
    const sourceRow = currentRevealRow;
    currentRevealRow = row;
    transitionGlobalReveal();
    openReveal(row, x, y, sourceRow);
  });

  index.addEventListener("pointerdown", (event) => {
    const row = event.target.closest(".project-row");
    if (row) warmProject(row);
  }, { passive: true });

  window.addEventListener("scroll", requestMobileProjectMotion, { passive: true });
  window.addEventListener("resize", requestMobileProjectMotion, { passive: true });
  mobileProjectMotion.addEventListener("change", requestMobileProjectMotion);
  window.addEventListener("pagehide", () => {
    clearTimeout(projectPlaybackTimer);
    playingProjectVideo = null;
    index.querySelectorAll("video").forEach((video) => video.pause());
  });
  window.addEventListener("pageshow", syncProjectPlayback);

  function restoreProjectAnchor() {
    const projectId = decodeURIComponent(window.location.hash.slice(1));
    const marker = document.getElementById(projectId);

    // Project rendering changes the footer's position, especially on phones.
    if (projectId === "contact") {
      marker?.scrollIntoView({ behavior: "instant", block: "start" });
      return;
    }

    const row = marker?.nextElementSibling;

    if (!row?.classList.contains("project-row")) {
      return;
    }

    row.scrollIntoView({ behavior: "instant", block: "center" });
    window.dispatchEvent(new Event("projectanchorrestore"));
  }

  renderProjectIndex();

  if (document.readyState === "complete") {
    restoreProjectAnchor();
  } else {
    window.addEventListener("load", restoreProjectAnchor, { once: true });
  }

  window.addEventListener("hashchange", restoreProjectAnchor);
})();
